import React from 'react'
import PropTypes from 'prop-types'

// Components
import {Box, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import {LinkBox, LinkOverlay} from '@chakra-ui/react'
import DynamicImage from '@app/components/dynamic-image'
import {useIntl} from 'react-intl'

// Hooks
import {useLinkUrl, LINK_TYPE} from '../../hooks/use-link-url'
import {useStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useGTM} from '@app/hooks/use-gtm'

// Constants
import {
    CATEGORY_BOTTOM_CONTENT_IMAGE_FIELD,
    CATEGORY_BOTTOM_CONTENT_IMAGE_TITLE_FIELD,
    CATEGORY_BOTTOM_CONTENT_IMAGE_ALT_FIELD,
    CATEGORY_BOTTOM_CONTENT_TITLE_FIELD,
    CATEGORY_BOTTOM_CONTENT_DESC_FIELD,
    CATEGORY_BOTTOM_CONTENT_LINKS_LABEL_FIELD,
    CATEGORY_BOTTOM_CONTENT_LINKS_URL_FIELD,
    CATEGORY_BOTTOM_CONTENT_LINKS_TYPE_FIELD
} from './constants'
import {DYNAMIC_IMAGE_WIDTHS} from '@app/constants.js'

// Utils
import {idFromString} from '../../utils/utils'

/**
 * Drawer menu footer of level 2 panel on mobile
 *
 * @param {Object} item - category item
 *
 * @returns {ReactElement}
 */
const DrawerMenuLvl2Footer = ({item, isOpen, categoryName, ...props}) => {
    const intl = useIntl()

    // Item props
    const image = item[CATEGORY_BOTTOM_CONTENT_IMAGE_FIELD]
    const imageTitle = item[CATEGORY_BOTTOM_CONTENT_IMAGE_TITLE_FIELD]
    const imageAlt = item[CATEGORY_BOTTOM_CONTENT_IMAGE_ALT_FIELD]
    const title = item[CATEGORY_BOTTOM_CONTENT_TITLE_FIELD]
    const description = item[CATEGORY_BOTTOM_CONTENT_DESC_FIELD]
    const linkType = item[CATEGORY_BOTTOM_CONTENT_LINKS_TYPE_FIELD]
    const linkURL = item[CATEGORY_BOTTOM_CONTENT_LINKS_URL_FIELD]
    const linkLabel = item[CATEGORY_BOTTOM_CONTENT_LINKS_LABEL_FIELD]

    // Hooks
    const linkBoxUrl = useLinkUrl(linkURL, linkType)
    const styles = useStyleConfig('Link', {})

    const titleId = idFromString(categoryName, 'footer-link', 'title')
    const descriptionId = idFromString(categoryName, 'footer-link', 'description')

    const linkProps =
        linkType === LINK_TYPE.EXTERNAL
            ? {isExternal: true, href: linkBoxUrl}
            : {as: RouteLink, to: linkBoxUrl}
    const {triggerClickImageMenuEvent} = useGTM()

    /**
     * Handles the click event for the Drawer Menu Level 2 footer.
     *
     * This function is typically used to trigger actions such as navigation,
     * closing the drawer, or executing custom logic when the footer area of
     * the level 2 drawer menu is clicked.
     *
     * @function handleOnClick
     * @param {React.MouseEvent} event - The click event object.
     * @returns {void}
     */
    const handleOnClick = (event) => {
        const target = event.target
        triggerClickImageMenuEvent(target.href)
    }

    const ariaLabel = intl.formatMessage(
        {
            id: 'drawer_menu.lvl2.link_aria_label',
            defaultMessage: "Go to ''{imageAlt}'' selection"
        },
        {
            imageAlt: imageAlt
        }
    )

    return (
        <LinkBox textAlign="center" position="relative" {...props}>
            <Box>
                {image && (
                    <DynamicImage
                        src={`${image}[?sw={width}]`}
                        widths={DYNAMIC_IMAGE_WIDTHS.thirdandfullonsmalldevice}
                        imageProps={{
                            width: '100%',
                            loading: 'auto',
                            fetchpriority: 'high',
                            alt: imageAlt === 'empty' ? '' : imageAlt || title,
                            title: imageTitle === 'empty' ? '' : imageTitle || title
                        }}
                    />
                )}
            </Box>

            {title && (
                <Text id={titleId} fontFamily="heading" fontSize="md" lineHeight="12px" mt="10px">
                    {title}
                </Text>
            )}
            {description && (
                <Text id={descriptionId} variant="bodySmall" color="darkGray" mt="2px">
                    {description}
                </Text>
            )}
            {linkURL && (
                <Box mt="10px">
                    <LinkOverlay
                        {...linkProps}
                        onClick={handleOnClick}
                        tabIndex={isOpen ? 0 : -1}
                        aria-label={ariaLabel}
                        {...styles}
                    >
                        {linkLabel && (
                            <Text variant="bodyBase1" as="span" textDecoration="underline">
                                {linkLabel}
                            </Text>
                        )}
                    </LinkOverlay>
                </Box>
            )}
        </LinkBox>
    )
}

DrawerMenuLvl2Footer.propTypes = {
    item: PropTypes.object,
    isOpen: PropTypes.bool,
    categoryName: PropTypes.string
}

export default DrawerMenuLvl2Footer
